import { useState, useEffect, useRef, useCallback } from "react";
import { useToast } from "@/components/SimpleToast";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Paperclip,
  UploadCloud,
  X,
  Download,
  FileText,
  FileArchive,
  FileSpreadsheet,
  Image as ImageIcon,
  File as FileIcon,
  Loader2,
} from "lucide-react";

const MAX_SIZE = 25 * 1024 * 1024;

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function iconFor(ct = "") {
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct === "application/pdf") return FileText;
  if (ct.includes("zip") || ct.includes("compressed")) return FileArchive;
  if (ct.includes("sheet") || ct.includes("excel") || ct.includes("csv")) return FileSpreadsheet;
  if (ct.includes("word") || ct.startsWith("text/")) return FileText;
  return FileIcon;
}

/**
 * Generic drag & drop attachments field backed by object storage.
 * `api` must provide: list(ownerId), upload(file, ownerId), delete(id), downloadUrl(id).
 */
export function AttachmentsField({ ownerId, api, onIdsChange, testidPrefix = "attachment", label = "Anhänge" }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const onIdsChangeRef = useRef(onIdsChange);
  onIdsChangeRef.current = onIdsChange;

  const emitIds = useCallback((list) => {
    const ids = list.map((a) => a.id);
    queueMicrotask(() => {
      if (onIdsChangeRef.current) onIdsChangeRef.current(ids);
    });
  }, []);

  useEffect(() => {
    let active = true;
    if (ownerId) {
      api
        .list(ownerId)
        .then((res) => {
          if (!active) return;
          const list = Array.isArray(res.data) ? res.data : [];
          setItems(list);
          emitIds(list);
        })
        .catch(() => {});
    } else {
      setItems([]);
      emitIds([]);
    }
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      if (file.size > MAX_SIZE) {
        toast.error(`"${file.name}" ist zu groß (max. 25 MB)`);
        continue;
      }
      try {
        const res = await api.upload(file, ownerId);
        setItems((prev) => {
          const next = [...prev, res.data];
          emitIds(next);
          return next;
        });
      } catch (e) {
        toast.error(`Hochladen fehlgeschlagen: ${file.name}`);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const removeItem = async (att) => {
    try {
      await api.delete(att.id);
      setItems((prev) => {
        const next = prev.filter((a) => a.id !== att.id);
        emitIds(next);
        return next;
      });
    } catch (e) {
      toast.error("Anhang konnte nicht gelöscht werden");
    }
  };

  return (
    <div className="space-y-2" data-testid={testidPrefix}>
      <Label className="flex items-center gap-1.5">
        <Paperclip className="w-4 h-4" />
        {label}
      </Label>

      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); inputRef.current?.click(); } }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        data-testid={`${testidPrefix}-dropzone`}
        className={`rounded-lg border-2 border-dashed p-4 text-center cursor-pointer transition-colors ${
          dragOver ? "border-primary bg-primary/10" : "border-muted-foreground/30 hover:border-primary/50"
        }`}
      >
        <UploadCloud className="w-6 h-6 mx-auto mb-1 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Dateien hierher ziehen oder <span className="text-primary font-medium">klicken</span>
        </p>
        <p className="text-xs text-muted-foreground/70 mt-0.5">Max. 25 MB pro Datei</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
          data-testid={`${testidPrefix}-input`}
        />
      </div>

      {uploading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid={`${testidPrefix}-uploading`}>
          <Loader2 className="w-4 h-4 animate-spin" /> Lädt hoch…
        </div>
      )}

      {items.length > 0 && (
        <ul className="space-y-1.5" data-testid={`${testidPrefix}-list`}>
          {items.map((att) => {
            const Icon = iconFor(att.content_type);
            return (
              <li
                key={att.id}
                className="flex items-center gap-2 p-2 rounded-lg border bg-muted/30"
                data-testid={`${testidPrefix}-item-${att.id}`}
              >
                {att.content_type?.startsWith("image/") ? (
                  <img
                    src={api.downloadUrl(att.id)}
                    alt={att.original_filename}
                    className="w-9 h-9 rounded object-cover shrink-0 border"
                  />
                ) : (
                  <span className="w-9 h-9 rounded bg-background border flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-muted-foreground" />
                  </span>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{att.original_filename}</p>
                  <p className="text-xs text-muted-foreground">{formatSize(att.size)}</p>
                </div>
                <a
                  href={api.downloadUrl(att.id)}
                  download={att.original_filename}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0"
                  data-testid={`${testidPrefix}-download-${att.id}`}
                  title="Herunterladen"
                >
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8">
                    <Download className="w-4 h-4" />
                  </Button>
                </a>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                  onClick={() => removeItem(att)}
                  data-testid={`${testidPrefix}-remove-${att.id}`}
                  title="Entfernen"
                >
                  <X className="w-4 h-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
